import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { paymentApi, settlementApi, depositApi, purchaseApi, generalTransactionApi, transactionCategoryApi, invoiceHistoryApi } from '../api';
import {
  PAYMENT_TYPES, PAYMENT_TYPE_COLORS,
  PAYMENT_STATUSES, PAYMENT_STATUS_COLORS,
  PAYMENT_METHODS,
  SETTLEMENT_STATUSES, SETTLEMENT_STATUS_COLORS,
  CUSTOMER_TYPES,
  DEPOSIT_TYPES, PURCHASE_CATEGORIES, PURCHASE_PAYMENT_STATUSES, PURCHASE_PAYMENT_STATUS_COLORS,
  TRANSACTION_TYPES, INVOICE_SEND_STATUSES, INVOICE_SEND_STATUS_COLORS,
} from '../constants';

type TabType = 'customerPayments' | 'payments' | 'refunds' | 'settlements'
  | 'customerDeposits' | 'purchases' | 'commissionExpenses' | 'invoiceHistory'
  | 'income' | 'expenses' | 'generalSummary' | 'categories';

export default function DressPayments() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('customerPayments');
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);

  // 고객매출내역 필터 (이번달 기본)
  const [summaryMonth, setSummaryMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [cpDateFrom, setCpDateFrom] = useState('');
  const [cpDateTo, setCpDateTo] = useState('');
  const [cpSettlementStatus, setCpSettlementStatus] = useState('');
  const [cpCustomerType, setCpCustomerType] = useState('');

  // 정산 탭 - 파트너 집계
  const [partnerSummaryData, setPartnerSummaryData] = useState<any[]>([]);
  const [isLoadingPartnerSummary, setIsLoadingPartnerSummary] = useState(false);

  // 결제 등록 모달
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    dressContractIdx: '', dressCustomerIdx: '', amount: '', paidDt: new Date().toISOString().split('T')[0],
    paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD',
    bankInfo: '', cashReceiptYn: 'n', taxInvoiceYn: 'n', memo: '',
  });

  // 환불 등록 모달
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundForm, setRefundForm] = useState({
    dressContractIdx: '', dressCustomerIdx: '', totalPaidAmount: '', penaltyRate: '',
    refundDt: new Date().toISOString().split('T')[0], refundMethod: '', bankInfo: '', reason: '', memo: '',
  });

  // 정산 등록 모달
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementForm, setSettlementForm] = useState({
    dressContractIdx: '', partnerVendorIdx: '', partnershipIdx: '',
    totalSalesAmount: '', commissionRate: '', bankName: '', accountNumber: '', memo: '',
  });

  // 입금 등록 모달
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositForm, setDepositForm] = useState({
    dressContractIdx: '', dressCustomerIdx: '', depositDt: new Date().toISOString().split('T')[0],
    amount: '', depositType: 'TRANSFER', bankName: '', accountNumber: '', depositorName: '', memo: '',
  });

  // 구매 등록 모달
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    supplierName: '', purchaseDt: new Date().toISOString().split('T')[0],
    itemName: '', itemCategory: 'DRESS', quantity: '1', unitPrice: '', totalAmount: '', memo: '',
  });

  // 수입/지출 등록 모달
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    transactionType: 'INCOME', categoryIdx: '', transactionDt: new Date().toISOString().split('T')[0],
    title: '', amount: '', paymentMethod: 'TRANSFER', counterparty: '', memo: '',
  });

  // 분류 등록 모달
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    categoryType: 'INCOME', name: '', sortOrder: '0',
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let result;
      if (activeTab === 'customerPayments') {
        // 월 기반 기본 날짜 계산 (필터가 비어있을 때)
        let dateFrom = cpDateFrom || undefined;
        let dateTo = cpDateTo || undefined;
        if (!dateFrom && !dateTo && summaryMonth) {
          const [y, m] = summaryMonth.split('-').map(Number);
          dateFrom = `${y}-${String(m).padStart(2, '0')}-01`;
          const lastDay = new Date(y, m, 0).getDate();
          dateTo = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        }
        result = await settlementApi.customerPayments({
          page, size: 20,
          dateFrom,
          dateTo,
          settlementStatus: cpSettlementStatus || undefined,
          customerType: cpCustomerType || undefined,
        });
      } else if (activeTab === 'payments') {
        result = await paymentApi.list({ page, size: 20 });
      } else if (activeTab === 'refunds') {
        result = await paymentApi.refundList({ page, size: 20 });
      } else if (activeTab === 'settlements') {
        result = await settlementApi.list({ page, size: 20 });
      } else if (activeTab === 'customerDeposits') {
        result = await depositApi.list({ page, size: 20 });
      } else if (activeTab === 'purchases') {
        result = await purchaseApi.list({ page, size: 20 });
      } else if (activeTab === 'commissionExpenses') {
        result = await settlementApi.list({ page, size: 20 });
      } else if (activeTab === 'invoiceHistory') {
        result = await invoiceHistoryApi.list({ page, size: 20 });
      } else if (activeTab === 'income') {
        result = await generalTransactionApi.list({ transactionType: 'INCOME', page, size: 20 });
      } else if (activeTab === 'expenses') {
        result = await generalTransactionApi.list({ transactionType: 'EXPENSE', page, size: 20 });
      } else if (activeTab === 'generalSummary') {
        result = await generalTransactionApi.summary({ page, size: 20 });
      } else if (activeTab === 'categories') {
        result = await transactionCategoryApi.list({});
      }
      setData(result.items || result || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page, cpDateFrom, cpDateTo, cpSettlementStatus, cpCustomerType, summaryMonth]);

  // 정산 탭일 때 파트너 집계 조회
  const fetchPartnerSummary = useCallback(async () => {
    if (activeTab !== 'settlements') return;
    setIsLoadingPartnerSummary(true);
    try {
      const result = await settlementApi.partnerSummary({ page: 1, size: 100 });
      setPartnerSummaryData(result.items || result || []);
    } catch (err) {
      console.error('파트너 집계 조회 실패:', err);
    } finally {
      setIsLoadingPartnerSummary(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchPartnerSummary(); }, [fetchPartnerSummary]);

  // 이번달 요약 계산
  const monthlySummary = (() => {
    if (activeTab !== 'customerPayments') return { totalContract: 0, totalPaid: 0, totalUnpaid: 0, count: 0 };
    let totalContract = 0;
    let totalPaid = 0;
    let totalUnpaid = 0;
    data.forEach((cp: any) => {
      totalContract += Number(cp.totalAmount || 0);
      totalPaid += Number(cp.contractPayment || 0) + Number(cp.intermediatePayment || 0) + Number(cp.balancePayment || 0);
      totalUnpaid += Number(cp.unpaidAmount || 0);
    });
    return { totalContract, totalPaid, totalUnpaid, count: data.length };
  })();

  // --- 인보이스 발송 ---
  const handleSendInvoice = async (idx: number) => {
    if (!confirm('인보이스를 발송하시겠습니까?')) return;
    try {
      await settlementApi.sendInvoice(idx);
      alert('인보이스가 발송되었습니다.');
      fetchData();
    } catch (err: any) {
      alert(`인보이스 발송 실패: ${err.message}`);
    }
  };

  // --- 결제 등록 ---
  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentForm.dressContractIdx || !paymentForm.amount) { alert('계약과 금액은 필수입니다.'); return; }
    try {
      await paymentApi.create({
        ...paymentForm,
        dressContractIdx: Number(paymentForm.dressContractIdx),
        dressCustomerIdx: Number(paymentForm.dressCustomerIdx),
        amount: Number(paymentForm.amount),
      });
      alert('결제가 등록되었습니다.');
      setShowPaymentModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 환불 등록 ---
  const handleCreateRefund = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!refundForm.dressContractIdx || !refundForm.totalPaidAmount) { alert('계약과 총결제금액은 필수입니다.'); return; }
    try {
      await paymentApi.refundCreate({
        ...refundForm,
        dressContractIdx: Number(refundForm.dressContractIdx),
        dressCustomerIdx: Number(refundForm.dressCustomerIdx),
        totalPaidAmount: Number(refundForm.totalPaidAmount),
        penaltyRate: refundForm.penaltyRate ? Number(refundForm.penaltyRate) : undefined,
      });
      alert('환불이 등록되었습니다.');
      setShowRefundModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 정산 등록 ---
  const handleCreateSettlement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settlementForm.dressContractIdx || !settlementForm.partnerVendorIdx || !settlementForm.totalSalesAmount || !settlementForm.commissionRate) {
      alert('필수 항목을 입력해주세요.'); return;
    }
    try {
      await settlementApi.create({
        ...settlementForm,
        dressContractIdx: Number(settlementForm.dressContractIdx),
        partnerVendorIdx: Number(settlementForm.partnerVendorIdx),
        partnershipIdx: settlementForm.partnershipIdx ? Number(settlementForm.partnershipIdx) : undefined,
        totalSalesAmount: Number(settlementForm.totalSalesAmount),
        commissionRate: Number(settlementForm.commissionRate),
      });
      alert('정산이 등록되었습니다.');
      setShowSettlementModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 정산 상태 변경 ---
  const handleSettlementStatusChange = async (idx: number, newStatus: string) => {
    try {
      const updateData: any = { status: newStatus };
      if (newStatus === 'SENT') updateData.sentDt = new Date().toISOString();
      await settlementApi.update(idx, updateData);
      fetchData();
    } catch (err: any) {
      alert(`상태 변경 실패: ${err.message}`);
    }
  };

  // --- 입금 등록 ---
  const handleCreateDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!depositForm.dressContractIdx || !depositForm.amount) { alert('계약과 금액은 필수입니다.'); return; }
    try {
      await depositApi.create({
        ...depositForm,
        dressContractIdx: Number(depositForm.dressContractIdx),
        dressCustomerIdx: Number(depositForm.dressCustomerIdx),
        amount: Number(depositForm.amount),
      });
      alert('입금이 등록되었습니다.');
      setShowDepositModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 구매 등록 ---
  const handleCreatePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!purchaseForm.supplierName || !purchaseForm.itemName || !purchaseForm.unitPrice) { alert('공급업체, 품목명, 단가는 필수입니다.'); return; }
    try {
      await purchaseApi.create({
        ...purchaseForm,
        quantity: Number(purchaseForm.quantity),
        unitPrice: Number(purchaseForm.unitPrice),
        totalAmount: Number(purchaseForm.quantity) * Number(purchaseForm.unitPrice),
      });
      alert('구매가 등록되었습니다.');
      setShowPurchaseModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 수입/지출 등록 ---
  const handleCreateTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transactionForm.title || !transactionForm.amount) { alert('제목과 금액은 필수입니다.'); return; }
    try {
      await generalTransactionApi.create({
        ...transactionForm,
        categoryIdx: transactionForm.categoryIdx ? Number(transactionForm.categoryIdx) : undefined,
        amount: Number(transactionForm.amount),
      });
      alert('거래가 등록되었습니다.');
      setShowTransactionModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 분류 등록 ---
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryForm.name) { alert('이름은 필수입니다.'); return; }
    try {
      await transactionCategoryApi.create({
        ...categoryForm,
        sortOrder: Number(categoryForm.sortOrder),
      });
      alert('분류가 등록되었습니다.');
      setShowCategoryModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 분류 삭제 ---
  const handleDeleteCategory = async (idx: number) => {
    if (!confirm('이 분류를 삭제하시겠습니까?')) return;
    try {
      await transactionCategoryApi.delete(idx);
      fetchData();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // 환불 자동계산
  const penaltyAmount = Number(refundForm.totalPaidAmount || 0) * Number(refundForm.penaltyRate || 0) / 100;
  const refundAmount = Number(refundForm.totalPaidAmount || 0) - penaltyAmount;

  // 정산 자동계산
  const commissionAmount = Number(settlementForm.totalSalesAmount || 0) * Number(settlementForm.commissionRate || 0) / 100;
  const supplyAmount = Number(settlementForm.totalSalesAmount || 0) - commissionAmount;
  const vatAmount = supplyAmount * 0.1;
  const settlementAmount = supplyAmount + vatAmount;

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">정산관리</h1>
            <p className="text-sm text-gray-600 mt-1">드레스샵 결제 및 정산을 관리합니다</p>
          </div>
          <div className="flex gap-3">
            {activeTab === 'payments' && (
              <button onClick={() => setShowPaymentModal(true)}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 결제 등록
              </button>
            )}
            {activeTab === 'refunds' && (
              <button onClick={() => setShowRefundModal(true)}
                className="bg-orange-600 text-white px-6 py-2.5 rounded-lg hover:bg-orange-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 환불 등록
              </button>
            )}
            {activeTab === 'settlements' && (
              <button onClick={() => setShowSettlementModal(true)}
                className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 정산 등록
              </button>
            )}
            {activeTab === 'customerDeposits' && (
              <button onClick={() => setShowDepositModal(true)}
                className="bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 입금 등록
              </button>
            )}
            {activeTab === 'purchases' && (
              <button onClick={() => setShowPurchaseModal(true)}
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 구매 등록
              </button>
            )}
            {activeTab === 'income' && (
              <button onClick={() => { setTransactionForm(f => ({ ...f, transactionType: 'INCOME' })); setShowTransactionModal(true); }}
                className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg hover:bg-emerald-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 수입 등록
              </button>
            )}
            {activeTab === 'expenses' && (
              <button onClick={() => { setTransactionForm(f => ({ ...f, transactionType: 'EXPENSE' })); setShowTransactionModal(true); }}
                className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 지출 등록
              </button>
            )}
            {activeTab === 'categories' && (
              <button onClick={() => setShowCategoryModal(true)}
                className="bg-violet-600 text-white px-6 py-2.5 rounded-lg hover:bg-violet-700 flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 분류 등록
              </button>
            )}
          </div>
        </div>

        {/* 월별 요약 */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => {
            const [y, m] = summaryMonth.split('-').map(Number);
            const prev = new Date(y, m - 2, 1);
            setSummaryMonth(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
            setPage(1);
          }} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
          </button>
          <span className="text-lg font-bold text-gray-800">
            {(() => { const [y, m] = summaryMonth.split('-').map(Number); return `${y}년 ${m}월`; })()}
          </span>
          <button onClick={() => {
            const [y, m] = summaryMonth.split('-').map(Number);
            const next = new Date(y, m, 1);
            setSummaryMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
            setPage(1);
          }} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
          </button>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-xs font-medium text-gray-500 mb-1">계약건수</p>
            <p className="text-2xl font-bold text-gray-900">{monthlySummary.count}<span className="text-sm font-normal text-gray-500 ml-1">건</span></p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-xs font-medium text-gray-500 mb-1">총 계약금액</p>
            <p className="text-2xl font-bold text-gray-900">{monthlySummary.totalContract.toLocaleString()}<span className="text-sm font-normal text-gray-500 ml-1">원</span></p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-xs font-medium text-gray-500 mb-1">총 납부액</p>
            <p className="text-2xl font-bold text-blue-600">{monthlySummary.totalPaid.toLocaleString()}<span className="text-sm font-normal text-blue-400 ml-1">원</span></p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-xs font-medium text-gray-500 mb-1">총 미납금</p>
            <p className="text-2xl font-bold text-red-600">{monthlySummary.totalUnpaid.toLocaleString()}<span className="text-sm font-normal text-red-400 ml-1">원</span></p>
          </div>
        </div>

        {/* 고객매출내역 필터 */}
        {activeTab === 'customerPayments' && (
          <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">계약일(시작)</label>
                <input type="date" value={cpDateFrom} onChange={(e) => setCpDateFrom(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">계약일(종료)</label>
                <input type="date" value={cpDateTo} onChange={(e) => setCpDateTo(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">정산상태</label>
                <select value={cpSettlementStatus} onChange={(e) => setCpSettlementStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">전체</option>
                  {SETTLEMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">고객구분</label>
                <select value={cpCustomerType} onChange={(e) => setCpCustomerType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">전체</option>
                  {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <button onClick={() => { setPage(1); fetchData(); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm cursor-pointer">
                <i className="ri-search-line mr-1"></i>조회
              </button>
              <button onClick={() => { setCpDateFrom(''); setCpDateTo(''); setCpSettlementStatus(''); setCpCustomerType(''); setPage(1); }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm cursor-pointer">
                초기화
              </button>
            </div>
          </div>
        )}

        {/* 컨텐츠 */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
          ) : (
            <>
              {/* 매출관리 목록 */}
              {activeTab === 'customerPayments' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">고객명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">업체명</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">계약금액</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">계약금</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">중도금</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">잔금</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">납부액</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">미납금</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">매출 내역이 없습니다.</td></tr>
                    ) : data.map((cp: any, idx: number) => {
                      const contractPay = Number(cp.contractPayment || 0);
                      const intermediatePay = Number(cp.intermediatePayment || 0);
                      const balancePay = Number(cp.balancePayment || 0);
                      const totalPaid = contractPay + intermediatePay + balancePay;
                      const unpaid = Number(cp.unpaidAmount || 0);
                      return (
                        <tr key={cp.dressContractIdx || idx} className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => cp.dressCustomerIdx && navigate(`/dress-shop/customer-detail?id=${cp.dressCustomerIdx}&tab=PAYMENTS`)}>
                          <td className="px-4 py-4 text-sm text-gray-700">{cp.contractDt || '-'}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 font-medium">{cp.customerName || '-'}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">{cp.partnerVendorName || '-'}</td>
                          <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{(cp.totalAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{contractPay.toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{intermediatePay.toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{balancePay.toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm font-semibold text-blue-600 text-right">{totalPaid.toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm font-semibold text-right">
                            <span className={unpaid > 0 ? 'text-red-600' : 'text-green-600'}>{unpaid > 0 ? unpaid.toLocaleString() + '원' : '완납'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* 결제 목록 */}
              {activeTab === 'payments' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">고객명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제유형</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">현금영수증</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">세금계산서</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">결제 내역이 없습니다.</td></tr>
                    ) : data.map((p: any) => (
                      <tr key={p.dressPaymentIdx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{p.paidDt}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{p.dressCustomer?.name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{p.dressContract?.contractNo || '-'}</td>
                        <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[p.paymentType] || ''}`}>{PAYMENT_TYPES.find(t => t.value === p.paymentType)?.label || p.paymentType}</span></td>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{(p.amount || 0).toLocaleString()}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{PAYMENT_METHODS.find(m => m.value === p.paymentMethod)?.label || p.paymentMethod || '-'}</td>
                        <td className="px-4 py-4 text-center text-sm">{p.cashReceiptYn === 'y' ? <span className="text-green-600">Y</span> : <span className="text-gray-400">N</span>}</td>
                        <td className="px-4 py-4 text-center text-sm">{p.taxInvoiceYn === 'y' ? <span className="text-green-600">Y</span> : <span className="text-gray-400">N</span>}</td>
                        <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[p.status] || ''}`}>{PAYMENT_STATUSES.find(s => s.value === p.status)?.label || p.status}</span></td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{p.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 환불 목록 */}
              {activeTab === 'refunds' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">환불일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">고객명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">총결제금액</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">위약금율</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">위약금</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">환불금액</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">환불수단</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">사유</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">환불 내역이 없습니다.</td></tr>
                    ) : data.map((r: any) => (
                      <tr key={r.dressRefundIdx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{r.refundDt}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{r.dressCustomer?.name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{r.dressContract?.contractNo || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(r.totalPaidAmount || 0).toLocaleString()}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{r.penaltyRate || 0}%</td>
                        <td className="px-4 py-4 text-sm text-red-600 text-right">{(r.penaltyAmount || 0).toLocaleString()}원</td>
                        <td className="px-4 py-4 text-sm font-semibold text-blue-600 text-right">{(r.refundAmount || 0).toLocaleString()}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{r.refundMethod || '-'}</td>
                        <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : r.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>{r.status}</span></td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{r.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 정산 목록 */}
              {activeTab === 'settlements' && (
                <>
                  {/* 파트너 집계 섹션 */}
                  <div className="border-b border-gray-200">
                    <div className="px-6 py-4 bg-gray-50">
                      <h3 className="text-sm font-bold text-gray-700"><i className="ri-group-line mr-1"></i>파트너 집계</h3>
                    </div>
                    {isLoadingPartnerSummary ? (
                      <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                    ) : partnerSummaryData.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500 text-sm">파트너 집계 내역이 없습니다.</div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">파트너명</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수수료</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">공급가</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">부가세</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">정산금액</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {partnerSummaryData.map((ps: any, idx: number) => (
                            <tr key={ps.partnerVendorIdx || idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{ps.partnerName || ps.partnerVendor?.name || '-'}</td>
                              <td className="px-4 py-3 text-sm text-red-600 text-right">{(ps.commissionAmount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{(ps.supplyAmount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{(ps.vatAmount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-3 text-sm font-semibold text-blue-600 text-right">{(ps.settlementAmount || 0).toLocaleString()}원</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 정산 상세 목록 */}
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700"><i className="ri-file-list-line mr-1"></i>정산 상세</h3>
                  </div>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">파트너 업체</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">총매출</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수수료율</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수수료</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">공급가</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">부가세</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">정산금액</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">액션</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.length === 0 ? (
                        <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">정산 내역이 없습니다.</td></tr>
                      ) : data.map((s: any) => (
                        <tr key={s.dressPartnerSettlementIdx} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-sm text-gray-700">{s.dressContract?.contractNo || '-'}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{s.partnerVendor?.name || '-'}</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.totalSalesAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{s.commissionRate || 0}%</td>
                          <td className="px-4 py-4 text-sm text-red-600 text-right">{(s.commissionAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.supplyAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.vatAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4 text-sm font-semibold text-blue-600 text-right">{(s.settlementAmount || 0).toLocaleString()}원</td>
                          <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${SETTLEMENT_STATUS_COLORS[s.status] || ''}`}>{SETTLEMENT_STATUSES.find(st => st.value === s.status)?.label || s.status}</span></td>
                          <td className="px-4 py-4">
                            <div className="flex gap-1">
                              {s.status === 'PENDING' && (
                                <button onClick={() => handleSettlementStatusChange(s.dressPartnerSettlementIdx, 'SENT')}
                                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">발송</button>
                              )}
                              {s.status === 'SENT' && (
                                <button onClick={() => handleSettlementStatusChange(s.dressPartnerSettlementIdx, 'COMPLETED')}
                                  className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer">정산완료</button>
                              )}
                              <button onClick={() => handleSendInvoice(s.dressPartnerSettlementIdx)}
                                className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 cursor-pointer">
                                <i className="ri-mail-send-line mr-1"></i>인보이스 발송
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* 고객입금내역 */}
              {activeTab === 'customerDeposits' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">입금일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">고객명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">입금액</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">입금유형</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">입금자명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">비고</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">고객입금 내역이 없습니다.</td></tr>
                    ) : data.map((d: any, idx: number) => (
                      <tr key={d.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{d.depositDt || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{d.dressCustomer?.name || d.customerName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{d.dressContract?.contractNo || '-'}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{(d.amount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{DEPOSIT_TYPES.find((t: any) => t.value === d.depositType)?.label || d.depositType || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{d.depositorName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{d.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 납품구매내역 */}
              {activeTab === 'purchases' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">구매일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">공급업체</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">품목명</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">품목구분</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수량</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">단가</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">합계</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제상태</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">납품구매 내역이 없습니다.</td></tr>
                    ) : data.map((p: any, idx: number) => (
                      <tr key={p.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{p.purchaseDt || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{p.supplierName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{p.itemName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{PURCHASE_CATEGORIES.find((c: any) => c.value === p.itemCategory)?.label || p.itemCategory || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(p.quantity || 0).toLocaleString('ko-KR')}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(p.unitPrice || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{(p.totalAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${PURCHASE_PAYMENT_STATUS_COLORS[p.paymentStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {PURCHASE_PAYMENT_STATUSES.find((s: any) => s.value === p.paymentStatus)?.label || p.paymentStatus || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 수수료지출 */}
              {activeTab === 'commissionExpenses' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">등록일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">파트너업체</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">매출액</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수수료율</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수수료</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">공급가</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">부가세</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">정산금</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500">수수료지출 내역이 없습니다.</td></tr>
                    ) : data.map((s: any, idx: number) => (
                      <tr key={s.dressPartnerSettlementIdx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{s.createdAt?.split('T')[0] || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{s.dressContract?.contractNo || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{s.partnerVendor?.name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.totalSalesAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{s.commissionRate || 0}%</td>
                        <td className="px-4 py-4 text-sm text-red-600 text-right">{(s.commissionAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.supplyAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{(s.vatAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm font-semibold text-blue-600 text-right">{(s.settlementAmount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${SETTLEMENT_STATUS_COLORS[s.status] || ''}`}>{SETTLEMENT_STATUSES.find(st => st.value === s.status)?.label || s.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* FAX전송 */}
              {activeTab === 'invoiceHistory' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">발송일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">수신유형</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">수신정보</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-500">FAX전송 내역이 없습니다.</td></tr>
                    ) : data.map((inv: any, idx: number) => (
                      <tr key={inv.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{inv.sentDt || inv.createdAt?.split('T')[0] || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{inv.recipientType || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{inv.recipientInfo || inv.recipientFax || '-'}</td>
                        <td className="px-4 py-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${INVOICE_SEND_STATUS_COLORS[inv.sendStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {INVOICE_SEND_STATUSES.find((s: any) => s.value === inv.sendStatus)?.label || inv.sendStatus || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{inv.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 수입내역 */}
              {activeTab === 'income' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">거래일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">분류</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">제목</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">거래처</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">수입 내역이 없습니다.</td></tr>
                    ) : data.map((t: any, idx: number) => (
                      <tr key={t.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{t.transactionDt || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.category?.name || t.categoryName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{t.title || '-'}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-emerald-600 text-right">{(t.amount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.paymentMethod || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.counterparty || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{t.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 지출내역 */}
              {activeTab === 'expenses' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">거래일</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">분류</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">제목</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">거래처</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">지출 내역이 없습니다.</td></tr>
                    ) : data.map((t: any, idx: number) => (
                      <tr key={t.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">{t.transactionDt || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.category?.name || t.categoryName || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{t.title || '-'}</td>
                        <td className="px-4 py-4 text-sm font-semibold text-red-600 text-right">{(t.amount || 0).toLocaleString('ko-KR')}원</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.paymentMethod || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">{t.counterparty || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-500 max-w-32 truncate">{t.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* 결산 */}
              {activeTab === 'generalSummary' && (
                <div className="p-6">
                  {data.length === 0 ? (
                    <div className="text-center text-gray-500 py-12">결산 데이터가 없습니다.</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-6 mb-8">
                        <div className="bg-emerald-50 rounded-lg p-6 text-center">
                          <div className="text-sm text-gray-600 mb-2">총 수입</div>
                          <div className="text-2xl font-bold text-emerald-600">
                            {(data[0]?.totalIncome || data.reduce((sum: number, d: any) => sum + (d.totalIncome || 0), 0) || 0).toLocaleString('ko-KR')}원
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-6 text-center">
                          <div className="text-sm text-gray-600 mb-2">총 지출</div>
                          <div className="text-2xl font-bold text-red-600">
                            {(data[0]?.totalExpense || data.reduce((sum: number, d: any) => sum + (d.totalExpense || 0), 0) || 0).toLocaleString('ko-KR')}원
                          </div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-6 text-center">
                          <div className="text-sm text-gray-600 mb-2">순이익</div>
                          <div className="text-2xl font-bold text-blue-600">
                            {(data[0]?.netProfit || ((data[0]?.totalIncome || 0) - (data[0]?.totalExpense || 0)) || 0).toLocaleString('ko-KR')}원
                          </div>
                        </div>
                      </div>
                      {data.length > 0 && data[0]?.items && (
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">기간</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수입</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">지출</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">순이익</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {data[0].items.map((item: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-4 text-sm text-gray-700">{item.period || '-'}</td>
                                <td className="px-4 py-4 text-sm text-emerald-600 text-right">{(item.income || 0).toLocaleString('ko-KR')}원</td>
                                <td className="px-4 py-4 text-sm text-red-600 text-right">{(item.expense || 0).toLocaleString('ko-KR')}원</td>
                                <td className="px-4 py-4 text-sm font-semibold text-blue-600 text-right">{((item.income || 0) - (item.expense || 0)).toLocaleString('ko-KR')}원</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 분류관리 */}
              {activeTab === 'categories' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">분류유형</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">이름</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">정렬순서</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">활성여부</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-500">분류 내역이 없습니다.</td></tr>
                    ) : data.map((c: any, idx: number) => (
                      <tr key={c.idx || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <span className={`text-xs px-2 py-1 rounded-full ${c.categoryType === 'INCOME' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {c.categoryType === 'INCOME' ? '수입' : '지출'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">{c.name || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-right">{c.sortOrder ?? '-'}</td>
                        <td className="px-4 py-4 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${c.activeYn === 'y' || c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.activeYn === 'y' || c.isActive ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button onClick={() => handleDeleteCategory(c.idx)}
                            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 cursor-pointer">삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 disabled:opacity-50 hover:bg-gray-50">이전</button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`px-3 py-2 rounded-lg text-sm ${page === p ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 disabled:opacity-50 hover:bg-gray-50">다음</button>
          </div>
        )}

        {/* 결제 등록 모달 */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">결제 등록</h2>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreatePayment} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계약 IDX *</label>
                    <input type="number" value={paymentForm.dressContractIdx} onChange={(e) => setPaymentForm({ ...paymentForm, dressContractIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객 IDX *</label>
                    <input type="number" value={paymentForm.dressCustomerIdx} onChange={(e) => setPaymentForm({ ...paymentForm, dressCustomerIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">금액 *</label>
                    <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required min={0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">결제일 *</label>
                    <input type="date" value={paymentForm.paidDt} onChange={(e) => setPaymentForm({ ...paymentForm, paidDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">결제유형 *</label>
                    <select value={paymentForm.paymentType} onChange={(e) => setPaymentForm({ ...paymentForm, paymentType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">결제수단</label>
                    <select value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">은행정보</label>
                  <input type="text" value={paymentForm.bankInfo} onChange={(e) => setPaymentForm({ ...paymentForm, bankInfo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={paymentForm.cashReceiptYn === 'y'} onChange={(e) => setPaymentForm({ ...paymentForm, cashReceiptYn: e.target.checked ? 'y' : 'n' })} className="rounded" />
                    <span className="text-sm text-gray-700">현금영수증</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={paymentForm.taxInvoiceYn === 'y'} onChange={(e) => setPaymentForm({ ...paymentForm, taxInvoiceYn: e.target.checked ? 'y' : 'n' })} className="rounded" />
                    <span className="text-sm text-gray-700">세금계산서</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={paymentForm.memo} onChange={(e) => setPaymentForm({ ...paymentForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 환불 등록 모달 */}
        {showRefundModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">환불 등록</h2>
                <button onClick={() => setShowRefundModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateRefund} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계약 IDX *</label>
                    <input type="number" value={refundForm.dressContractIdx} onChange={(e) => setRefundForm({ ...refundForm, dressContractIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객 IDX *</label>
                    <input type="number" value={refundForm.dressCustomerIdx} onChange={(e) => setRefundForm({ ...refundForm, dressCustomerIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">총 결제금액 *</label>
                  <input type="number" value={refundForm.totalPaidAmount} onChange={(e) => setRefundForm({ ...refundForm, totalPaidAmount: e.target.value })} required min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">위약금율 (%)</label>
                  <input type="number" value={refundForm.penaltyRate} onChange={(e) => setRefundForm({ ...refundForm, penaltyRate: e.target.value })} min={0} max={100} step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {/* 자동 계산 표시 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">위약금</div>
                    <div className="text-lg font-semibold text-red-600">{penaltyAmount.toLocaleString()}원</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">환불금액</div>
                    <div className="text-lg font-semibold text-blue-600">{refundAmount.toLocaleString()}원</div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">환불일 *</label>
                  <input type="date" value={refundForm.refundDt} onChange={(e) => setRefundForm({ ...refundForm, refundDt: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">환불수단</label>
                  <input type="text" value={refundForm.refundMethod} onChange={(e) => setRefundForm({ ...refundForm, refundMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">환불사유</label>
                  <textarea value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowRefundModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 정산 등록 모달 */}
        {showSettlementModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">정산 등록</h2>
                <button onClick={() => setShowSettlementModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateSettlement} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계약 IDX *</label>
                    <input type="number" value={settlementForm.dressContractIdx} onChange={(e) => setSettlementForm({ ...settlementForm, dressContractIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">파트너 업체 IDX *</label>
                    <input type="number" value={settlementForm.partnerVendorIdx} onChange={(e) => setSettlementForm({ ...settlementForm, partnerVendorIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">총 매출금액 *</label>
                    <input type="number" value={settlementForm.totalSalesAmount} onChange={(e) => setSettlementForm({ ...settlementForm, totalSalesAmount: e.target.value })} required min={0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수수료율(%) *</label>
                    <input type="number" value={settlementForm.commissionRate} onChange={(e) => setSettlementForm({ ...settlementForm, commissionRate: e.target.value })} required min={0} max={100} step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                {/* 자동 계산 표시 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">수수료</div>
                    <div className="text-sm font-semibold text-red-600">{commissionAmount.toLocaleString()}원</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">공급가</div>
                    <div className="text-sm font-semibold text-gray-800">{supplyAmount.toLocaleString()}원</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">부가세(10%)</div>
                    <div className="text-sm font-semibold text-gray-800">{vatAmount.toLocaleString()}원</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">정산금액</div>
                    <div className="text-sm font-semibold text-blue-600">{settlementAmount.toLocaleString()}원</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                    <input type="text" value={settlementForm.bankName} onChange={(e) => setSettlementForm({ ...settlementForm, bankName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계좌번호</label>
                    <input type="text" value={settlementForm.accountNumber} onChange={(e) => setSettlementForm({ ...settlementForm, accountNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={settlementForm.memo} onChange={(e) => setSettlementForm({ ...settlementForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowSettlementModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* 입금 등록 모달 */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">입금 등록</h2>
                <button onClick={() => setShowDepositModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateDeposit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계약 IDX *</label>
                    <input type="number" value={depositForm.dressContractIdx} onChange={(e) => setDepositForm({ ...depositForm, dressContractIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객 IDX *</label>
                    <input type="number" value={depositForm.dressCustomerIdx} onChange={(e) => setDepositForm({ ...depositForm, dressCustomerIdx: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">입금액 *</label>
                    <input type="number" value={depositForm.amount} onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })} required min={0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">입금일 *</label>
                    <input type="date" value={depositForm.depositDt} onChange={(e) => setDepositForm({ ...depositForm, depositDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">입금유형</label>
                  <select value={depositForm.depositType} onChange={(e) => setDepositForm({ ...depositForm, depositType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {DEPOSIT_TYPES.map((t: any) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                    <input type="text" value={depositForm.bankName} onChange={(e) => setDepositForm({ ...depositForm, bankName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">계좌번호</label>
                    <input type="text" value={depositForm.accountNumber} onChange={(e) => setDepositForm({ ...depositForm, accountNumber: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">입금자명</label>
                  <input type="text" value={depositForm.depositorName} onChange={(e) => setDepositForm({ ...depositForm, depositorName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={depositForm.memo} onChange={(e) => setDepositForm({ ...depositForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowDepositModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 구매 등록 모달 */}
        {showPurchaseModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">구매 등록</h2>
                <button onClick={() => setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreatePurchase} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">공급업체 *</label>
                    <input type="text" value={purchaseForm.supplierName} onChange={(e) => setPurchaseForm({ ...purchaseForm, supplierName: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">구매일 *</label>
                    <input type="date" value={purchaseForm.purchaseDt} onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">품목명 *</label>
                    <input type="text" value={purchaseForm.itemName} onChange={(e) => setPurchaseForm({ ...purchaseForm, itemName: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">품목구분</label>
                    <select value={purchaseForm.itemCategory} onChange={(e) => setPurchaseForm({ ...purchaseForm, itemCategory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {PURCHASE_CATEGORIES.map((c: any) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수량 *</label>
                    <input type="number" value={purchaseForm.quantity} onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity: e.target.value })} required min={1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">단가 *</label>
                    <input type="number" value={purchaseForm.unitPrice} onChange={(e) => setPurchaseForm({ ...purchaseForm, unitPrice: e.target.value })} required min={0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">합계금액</div>
                  <div className="text-lg font-semibold text-blue-600">{(Number(purchaseForm.quantity || 0) * Number(purchaseForm.unitPrice || 0)).toLocaleString('ko-KR')}원</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={purchaseForm.memo} onChange={(e) => setPurchaseForm({ ...purchaseForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowPurchaseModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 수입/지출 등록 모달 */}
        {showTransactionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">{transactionForm.transactionType === 'INCOME' ? '수입' : '지출'} 등록</h2>
                <button onClick={() => setShowTransactionModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateTransaction} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">거래유형</label>
                    <select value={transactionForm.transactionType} onChange={(e) => setTransactionForm({ ...transactionForm, transactionType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {TRANSACTION_TYPES.map((t: any) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">거래일 *</label>
                    <input type="date" value={transactionForm.transactionDt} onChange={(e) => setTransactionForm({ ...transactionForm, transactionDt: e.target.value })} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">분류 IDX</label>
                  <input type="number" value={transactionForm.categoryIdx} onChange={(e) => setTransactionForm({ ...transactionForm, categoryIdx: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                  <input type="text" value={transactionForm.title} onChange={(e) => setTransactionForm({ ...transactionForm, title: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">금액 *</label>
                    <input type="number" value={transactionForm.amount} onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })} required min={0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">결제수단</label>
                    <input type="text" value={transactionForm.paymentMethod} onChange={(e) => setTransactionForm({ ...transactionForm, paymentMethod: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">거래처</label>
                  <input type="text" value={transactionForm.counterparty} onChange={(e) => setTransactionForm({ ...transactionForm, counterparty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={transactionForm.memo} onChange={(e) => setTransactionForm({ ...transactionForm, memo: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowTransactionModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className={`px-6 py-2 text-white rounded-lg cursor-pointer ${transactionForm.transactionType === 'INCOME' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}>등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 분류 등록 모달 */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">분류 등록</h2>
                <button onClick={() => setShowCategoryModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateCategory} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">분류유형 *</label>
                  <select value={categoryForm.categoryType} onChange={(e) => setCategoryForm({ ...categoryForm, categoryType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="INCOME">수입</option>
                    <option value="EXPENSE">지출</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이름 *</label>
                  <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">정렬순서</label>
                  <input type="number" value={categoryForm.sortOrder} onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: e.target.value })} min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowCategoryModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
